import { useEffect, memo, CSSProperties } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectValidParams, selectGridData, selectShowReasonColumn } from '@pages/reporting-pages/reduced-materials/store/selectors';
import { createGridData } from '@pages/reporting-pages/reduced-materials/store/slice';
import { useLazyReducedMaterialsQuery } from '@api/order-reporting/api';
import { GridLoading, GridError, GridEmpty, GridNoParams } from 'components/global/GridElements';
import { useGridSorting } from 'components/filtering/filter-logic/useGridSorting';
import { useGridFiltering } from 'components/filtering/filter-logic/useGridFiltering';
import GridHeader from '@pages/reporting-pages/reduced-materials/grid/GridHeader';
import GridSummary from '@pages/reporting-pages/reduced-materials/grid/GridSummary';
import GridRow from '@pages/reporting-pages/reduced-materials/grid/GridRow';
import { ReducedMaterialsReport } from '@api/order-reporting/types/ReducedMaterialsReport';
import { getHeadersObject } from '@pages/reporting-pages/reduced-materials/headersObject';



const Grid = () => {

	const validParams = useSelector(selectValidParams);
	const gridData = useSelector(selectGridData);
	const showReasonColumn = useSelector(selectShowReasonColumn);
	const [ runQuery, { data, isLoading, isFetching, isError, isSuccess } ] = useLazyReducedMaterialsQuery();
	const [ filteredData, filterObject, handleSearchValues, handleAddFilterValues, clearFilter ] = useGridFiltering(gridData);
	const [ sortedData, sortObject, handleSorting, clearSorting ] = useGridSorting(filteredData);

	const dispatch = useDispatch();
	const runCreateGridData = (data: ReducedMaterialsReport[] | undefined | null) => dispatch(createGridData(data));

	const className = 'app-grid reduced-materials-grid';
	const style: CSSProperties = {
		gridTemplateColumns: getHeadersObject(showReasonColumn).map(x => x.width).join(' '),
	}


	useEffect(() => {
		if (validParams) {
			runQuery({ ...validParams }, true);
		}
	}, [ validParams ]);


	useEffect(() => {
		if (data) {
			runCreateGridData(data);
		}
	}, [ data ]);


	if (!validParams) return (
		<div className={className} style={style}>
			<GridHeader showReasonColumn={showReasonColumn} />
			<GridNoParams headersLength={getHeadersObject(showReasonColumn).length} />
		</div>
	);

	if (isLoading || isFetching) return (
		<div className={className} style={style}>
			<GridHeader showReasonColumn={showReasonColumn} />
			<GridLoading headersLength={getHeadersObject(showReasonColumn).length} />
		</div>
	);

	if (isError || !data) return (
		<div className={className} style={style}>
			<GridHeader showReasonColumn={showReasonColumn} />
			<GridError headersLength={getHeadersObject(showReasonColumn).length} />
		</div>
	);
	
	if (isSuccess && sortedData && sortedData.length === 0) return (
		<div className={className} style={style}>
			<GridHeader
				showReasonColumn={showReasonColumn}
				filterObject={filterObject}
				sortObject={sortObject}
				onSearchValues={handleSearchValues}
				onAddFilterValues={handleAddFilterValues}
				onClearFilter={clearFilter}
				onSorting={handleSorting}
				onClearSorting={clearSorting}
			/>
			<GridEmpty headersLength={getHeadersObject(showReasonColumn).length} />
		</div>
	);

	return (
		<div className={className} style={style}>
			
			<GridHeader
				showReasonColumn={showReasonColumn}
				filterObject={filterObject}
				sortObject={sortObject}
				onSearchValues={handleSearchValues}
				onAddFilterValues={handleAddFilterValues}
				onClearFilter={clearFilter}
				onSorting={handleSorting}
				onClearSorting={clearSorting}
			/>

			<GridSummary
				data={sortedData}
				showReasonColumn={showReasonColumn}
			/>
				
			{sortedData && sortedData.map((x,i) =>
				<GridRow
					key={`id-reduced-materials-grid-${
							x.orderUnversionedSystemId ? x.orderUnversionedSystemId : 'without-system-id'
						}-${
							x.orderDestinationId ? x.orderDestinationId : 'without-customer-id'
						}-${
							x.materialId ? x.materialId : 'without-material-id'
						}`}
					item={x}
					rowIndex={i}
					validParams={validParams}
					showReasonColumn={showReasonColumn}
				/>
			)}
		</div>
	)
}

export default memo(Grid);